import api from "@/api";
import { useToast } from 'vue-toastification';

const toast = useToast();

export default {
  namespaced: true,
  state: () => ({
    chapters: [],
    currentChapter: null,
    isLoading: false,
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 10,
      from: 0,
      to: 0
    }
  }),
  mutations: {
    SET_CHAPTERS(state, chapters) {
      state.chapters = chapters;
    },
    SET_CURRENT_CHAPTER(state, chapter) {
      state.currentChapter = chapter;
    },
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_PAGINATION(state, { currentPage, totalPages, totalItems, perPage, from, to }) {
      state.pagination = {
        currentPage,
        totalPages,
        totalItems,
        perPage,
        from,
        to
      };
    },
    ADD_CHAPTER(state, newChapter) {
      state.chapters.unshift(newChapter);
    },
    UPDATE_CHAPTER(state, updatedChapter) {
      const index = state.chapters.findIndex(c => c.id === updatedChapter.id);
      if (index !== -1) {
        state.chapters.splice(index, 1, updatedChapter);
      }
    },
    DELETE_CHAPTER(state, chapterId) {
      state.chapters = state.chapters.filter(c => c.id !== chapterId);
    },
    RESET_CURRENT_CHAPTER(state) {
      state.currentChapter = null;
    },
    SET_ITEMS(state, items) {
      state.items = items; 
    },
  },
  actions: {
    async fetchChapters({ commit }, { page = 1, per_page = 10, search = '' } = {}) {
      try {
        commit('SET_LOADING', true);
        const response = await api.get('/chapters', {
          params: { page, per_page, search }
        });
        commit('SET_ITEMS', response.data.data);
        const responseData = response.data?.data || [];
        const meta = response.data?.meta || {
          current_page: page,
          last_page: 1,
          total: responseData.length,
          per_page,
          from: (page - 1) * per_page + 1,
          to: Math.min(page * per_page, responseData.length)
        };
    
        commit('SET_CHAPTERS', responseData);
        commit('SET_PAGINATION', {
          currentPage: meta.current_page,
          totalPages: meta.last_page,
          totalItems: meta.total,
          perPage: meta.per_page,
          from: meta.from,
          to: meta.to
        });
        
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        toast.error(error.response?.data?.message || 'Failed to fetch chapters');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async fetchChapter({ commit }, chapterId) {
      try {
        commit('SET_LOADING', true);
        const response = await api.get(`/chapters/${chapterId}`);
        commit('SET_CURRENT_CHAPTER', response.data);
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        toast.error(error.response?.data?.message || 'Failed to fetch chapter');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createChapter({ commit }, chapterData) {
      try {
        commit('SET_LOADING', true);
        const response = await api.post('/chapters', chapterData);
        commit('ADD_CHAPTER', response.data);
        toast.success('Chapter created successfully');
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        toast.error(error.response?.data?.message || 'Failed to create chapter');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateChapter({ commit }, { chapterId, chapterData }) {
      try {
        commit('SET_LOADING', true);
        const response = await api.put(`/chapters/${chapterId}`, chapterData);
        commit('UPDATE_CHAPTER', response.data);
        commit('SET_CURRENT_CHAPTER', response.data);
        toast.success('Chapter updated successfully');
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        toast.error(error.response?.data?.message || 'Failed to update chapter');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteChapter({ commit }, chapterId) {
      try {
        commit('SET_LOADING', true);
        await api.delete(`/chapters/${chapterId}`);
        commit('DELETE_CHAPTER', chapterId);
        commit('RESET_CURRENT_CHAPTER');
        toast.success('Chapter deleted successfully');
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        toast.error(error.response?.data?.message || 'Failed to delete chapter');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    clearError({ commit }) {
      commit('SET_ERROR', null);
    }
  },
  getters: {
    getChapters: state => state.chapters,
    getChapterById: state => id => state.chapters.find(c => c.id === id),
    getCurrentChapter: state => state.currentChapter,
    isLoading: state => state.isLoading,
    getError: state => state.error,
    getPagination: state => state.pagination,
    activeChapters: state => state.chapters.filter(c => c.status === 'active'),
    inactiveChapters: state => state.chapters.filter(c => c.status === 'inactive')
  }
};